// client/src/pages/Orders.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Package, Eye, AlertCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import storeService, { Order,  Pagination } from '@/api/services/storeService';
import { getErrorMessage } from '@/utils/errorUtils';

const STATUS_FILTERS = ['all', 'pending', 'processing', 'completed', 'cancelled'] as const;
type FilterStatus = typeof STATUS_FILTERS[number];

const Orders = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [orders, setOrders]           = useState<Order[] | null>(null);
  const [pagination, setPagination]   = useState<Pagination | null>(null);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('all');
  const [currentPage, setCurrentPage] = useState(1);

  // ── Fetch orders ─────────────────────────────────────────────────────────
  const fetchOrders = useCallback(async (status: FilterStatus, page: number) => {
    setLoading(true);
    setError(null);
    try {
      const result = await storeService.getOrders({
        status: status === 'all' ? '' : status,
        page,
        limit: 10,
      });
      setOrders(result.orders);
      setPagination(result.pagination);
    } catch (err: unknown) {
      setError(getErrorMessage(err) ?? t('operator.commerce.orders.fetchError', {
        defaultValue: 'Failed to load orders. Please try again.',
      }));
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchOrders(filterStatus, currentPage);
  }, [fetchOrders, filterStatus, currentPage]);

  const handleFilterChange = (status: FilterStatus) => {
    setFilterStatus(status);
    setCurrentPage(1);
  };

  // ── Helpers ───────────────────────────────────────────────────────────────
  const getStatusStyle = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':  return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
      case 'processing': return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
      case 'pending':    return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'cancelled':  return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
      default:           return 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400';
    }
  };

  const formatMoney = (value: unknown) => {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue.toFixed(2) : '0.00';
  };

  const totalPages = pagination?.totalPages ?? 1;

  // ── Loading ───────────────────────────────────────────────────────────────
  if (loading && orders === null) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-black flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{t('operator.commerce.orders.meta.title', { defaultValue: 'My Orders | KonectX' })}</title>
      </Helmet>

      <div className="min-h-screen bg-gray-50 dark:bg-black">
        <div className="max-w-7xl mx-auto px-4 py-8">

          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-800 dark:text-white mb-2">
              {t('operator.commerce.orders.title', { defaultValue: 'My Orders' })}
            </h1>
            <p className="text-gray-500 dark:text-gray-400 text-sm">
              {t('operator.commerce.orders.subtitle', { defaultValue: 'Track and manage your orders' })}
            </p>
          </div>

          {/* Filter Tabs */}
          <div className="bg-white dark:bg-[#333436] rounded-xl p-4 mb-6">
            <div className="flex flex-wrap gap-2">
              {STATUS_FILTERS.map((status) => (
                <button
                  key={status}
                  onClick={() => handleFilterChange(status)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors capitalize ${
                    filterStatus === status
                      ? 'bg-gray-800 dark:bg-blue-700 text-white'
                      : 'bg-gray-100 dark:bg-[#1a1b1e] text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-[#252628]'
                  }`}
                >
                  {t(`operator.commerce.orders.filter.${status}`, { defaultValue: status.charAt(0).toUpperCase() + status.slice(1) })}
                </button>
              ))}
            </div>
          </div>

          {/* Error state */}
          {error && (
            <div className="bg-white dark:bg-[#333436] rounded-xl p-12 text-center">
              <AlertCircle className="w-14 h-14 text-red-400 mx-auto mb-4" />
              <p className="text-red-500 dark:text-red-400 mb-4">{error}</p>
              <button
                onClick={() => fetchOrders(filterStatus, currentPage)}
                className="px-6 py-2.5 bg-gray-800 dark:bg-blue-700 text-white rounded-xl text-sm font-semibold"
              >
                {t('operator.commerce.common.tryAgain', { defaultValue: 'Try Again' })}
              </button>
            </div>
          )}

          {/* Loading overlay for filter/page changes */}
          {loading && orders !== null && (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
            </div>
          )}

          {/* Empty state */}
          {!loading && !error && orders?.length === 0 && (
            <div className="bg-white dark:bg-[#333436] rounded-xl p-12 text-center">
              <Package className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
              <h2 className="text-2xl font-semibold text-gray-800 dark:text-white mb-2">
                {t('operator.commerce.orders.emptyTitle', { defaultValue: 'No orders found' })}
              </h2>
              <p className="text-gray-500 dark:text-gray-400 mb-6">
                {filterStatus === 'all'
                  ? t('operator.commerce.orders.emptyAll', { defaultValue: "You haven't placed any orders yet" })
                  : t('operator.commerce.orders.emptyStatus', {
                      defaultValue: 'No {{status}} orders found',
                      status: filterStatus,
                    })}
              </p>
              <button
                onClick={() => navigate('/operator/store')}
                className="px-8 py-3 bg-gray-800 dark:bg-blue-700 text-white rounded-xl font-semibold hover:bg-gray-900 dark:hover:bg-blue-600 transition-colors"
              >
                {t('operator.commerce.common.startShopping', { defaultValue: 'Start Shopping' })}
              </button>
            </div>
          )}

          {/* Orders list */}
          {!loading && !error && orders && orders.length > 0 && (
            <>
              <div className="space-y-4">
                {orders.map((order) => (
                  <div
                    key={order.id}
                    className="bg-white dark:bg-[#333436] rounded-xl p-6 hover:shadow-lg transition-shadow"
                  >
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-4">
                      <div>
                        <div className="flex items-center gap-3 mb-1.5">
                          <h3 className="text-lg font-semibold text-gray-800 dark:text-white">
                            {t('operator.commerce.orders.orderHash', { defaultValue: 'Order #{{num}}', num: order.orderNumber })}
                          </h3>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${getStatusStyle(order.status)}`}>
                            {order.status}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {t('operator.commerce.orders.placedOn', {
                            defaultValue: 'Placed on {{date}}',
                            date: order.placedAtFormatted,
                          })}
                        </p>
                      </div>

                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">
                            {t('operator.commerce.common.totalAmount', { defaultValue: 'Total Amount' })}
                          </p>
                          <p className="text-2xl font-bold text-gray-800 dark:text-white">
                            ${formatMoney(order.total)}
                            <span className="text-sm font-normal text-gray-400 ml-1">{order.currency || 'USD'}</span>
                          </p>
                        </div>
                        <button
                          onClick={() => navigate(`/operator/orders/${order.id}`)}
                          className="p-3 bg-gray-800 dark:bg-blue-700 text-white rounded-xl hover:bg-gray-900 dark:hover:bg-blue-600 transition-colors"
                          aria-label="View order details"
                        >
                          <Eye className="w-5 h-5" />
                        </button>
                      </div>
                    </div>

                    {/* Item summary chips */}
                    <div className="border-t border-gray-100 dark:border-gray-700 pt-4">
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                        {t('operator.commerce.common.itemCount', { defaultValue: '{{count}} items', count: order.itemCount })}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {order.items.slice(0, 3).map((item, i) => (
                          <div
                            key={i}
                            className="px-3 py-1 bg-gray-100 dark:bg-[#1a1b1e] rounded-lg text-xs text-gray-600 dark:text-gray-300"
                          >
                            {t('operator.commerce.common.qty', { defaultValue: 'Qty' })}: {item.quantity ?? 0} × ${formatMoney(item.unitPrice)}
                          </div>
                        ))}
                        {order.items.length > 3 && (
                          <div className="px-3 py-1 bg-gray-100 dark:bg-[#1a1b1e] rounded-lg text-xs text-gray-500 dark:text-gray-400">
                            +{order.items.length - 3} {t('operator.commerce.common.more', { defaultValue: 'more' })}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="mt-8 flex items-center justify-center gap-2">
                  <button
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(p => p - 1)}
                    className="p-2 border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-40 transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`w-9 h-9 rounded-lg text-sm font-medium transition-colors ${
                        page === currentPage
                          ? 'bg-blue-600 text-white'
                          : 'border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(p => p + 1)}
                    className="p-2 border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-40 transition-colors"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default Orders;
